import { useEffect, useState } from 'react';
import { api } from '../../utils/apiClient';

interface Category { id: string; name: string; sortOrder: number; }

interface Props {
  onClose: () => void;
  /** Called after any mutation so the parent can refresh its category caches. */
  onChanged?: () => void;
}

// Compact modal for managing the network's menu categories: order (drives the
// customer menu section order), rename, create, delete. Product↔category
// assignment lives on the product cards, not here.
export default function NetworkCatalogPanel({ onClose, onChanged }: Props) {
  const [cats, setCats] = useState<Category[]>([]);
  const [newName, setNewName] = useState('');
  const [err, setErr] = useState('');

  const load = () => api.get<{ categories: Category[] }>('/api/menu-catalog')
    .then(({ data }) => setCats(data.categories)).catch(() => setErr('Не удалось загрузить'));
  useEffect(() => { load(); }, []);

  const mutated = () => { load(); onChanged?.(); };

  const add = async () => {
    const name = newName.trim(); if (!name) return;
    await api.post('/api/menu-catalog/categories', { name }); setNewName(''); mutated();
  };
  const rename = async (id: string, name: string) => { await api.patch(`/api/menu-catalog/categories/${id}`, { name }); mutated(); };
  const remove = async (id: string) => { await api.delete(`/api/menu-catalog/categories/${id}`); mutated(); };
  const move = async (i: number, dir: -1 | 1) => {
    const next = [...cats]; const j = i + dir; if (j < 0 || j >= next.length) return;
    [next[i], next[j]] = [next[j], next[i]];
    setCats(next);
    await api.post('/api/menu-catalog/categories/reorder', { ids: next.map(c => c.id) });
    onChanged?.();
  };

  const s = {
    overlay: { position: 'fixed', inset: 0, background: 'rgba(0,0,0,.35)', zIndex: 1000, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16 } as const,
    modal: { background: '#fff', borderRadius: 16, padding: '20px 22px', width: '100%', maxWidth: 480, maxHeight: '80vh', overflowY: 'auto', boxShadow: '0 12px 40px rgba(0,0,0,.18)' } as const,
    head: { display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 4 } as const,
    title: { fontSize: 15, fontWeight: 700, color: '#1f1b16', margin: 0 } as const,
    hint: { fontSize: 12, color: '#a09888', margin: '0 0 14px' } as const,
    closeBtn: { border: 'none', background: 'none', fontSize: 18, color: '#999', cursor: 'pointer', padding: 4 } as const,
    addRow: { display: 'flex', gap: 8, marginBottom: 14 } as const,
    input: { flex: 1, padding: '9px 12px', borderRadius: 10, border: '1px solid #e0dcd5', fontSize: 14, outline: 'none', background: '#fafaf8' } as const,
    addBtn: { padding: '9px 16px', borderRadius: 10, border: 'none', background: '#1f1b16', color: '#fff', fontWeight: 600, fontSize: 13, cursor: 'pointer', whiteSpace: 'nowrap' } as const,
    row: { display: 'flex', alignItems: 'center', gap: 6, padding: '6px 8px', borderRadius: 10, background: '#fafaf8', marginBottom: 5, border: '1px solid #f0ede8' } as const,
    orderNum: { width: 20, textAlign: 'center', fontSize: 11, color: '#a09888', fontWeight: 600, flexShrink: 0 } as const,
    arrowBtn: { width: 26, height: 26, borderRadius: 6, border: '1px solid #e0dcd5', background: '#fff', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 12, color: '#666', flexShrink: 0 } as const,
    nameInput: { flex: 1, padding: '5px 9px', borderRadius: 8, border: '1px solid transparent', fontSize: 14, background: 'transparent', outline: 'none', color: '#1f1b16' } as const,
    delBtn: { padding: '4px 8px', borderRadius: 6, border: 'none', background: 'transparent', color: '#c0a0a0', fontSize: 12, cursor: 'pointer' } as const,
  };

  return (
    <div style={s.overlay} onClick={onClose}>
      <div style={s.modal} onClick={(e) => e.stopPropagation()}>
        <div style={s.head}>
          <h3 style={s.title}>Категории меню сети</h3>
          <button onClick={onClose} style={s.closeBtn}>✕</button>
        </div>
        <p style={s.hint}>Порядок здесь = порядок секций в меню гостя</p>
        {err && <div style={{ color: '#b44', fontSize: 13, marginBottom: 10 }}>{err}</div>}
        <div style={s.addRow}>
          <input value={newName} onChange={e => setNewName(e.target.value)} placeholder="Новая категория"
            onKeyDown={e => e.key === 'Enter' && add()} style={s.input} />
          <button onClick={add} style={s.addBtn}>+ Добавить</button>
        </div>
        {cats.map((c, i) => (
          <div key={c.id} style={s.row}>
            <span style={s.orderNum}>{i + 1}</span>
            <button onClick={() => move(i, -1)} disabled={i === 0}
              style={{ ...s.arrowBtn, ...(i === 0 ? { opacity: 0.3, cursor: 'default' } : {}) }}>↑</button>
            <button onClick={() => move(i, 1)} disabled={i === cats.length - 1}
              style={{ ...s.arrowBtn, ...(i === cats.length - 1 ? { opacity: 0.3, cursor: 'default' } : {}) }}>↓</button>
            <input defaultValue={c.name}
              onBlur={e => { if (e.target.value.trim() && e.target.value !== c.name) rename(c.id, e.target.value.trim()); }}
              style={s.nameInput} />
            <button onClick={() => remove(c.id)}
              onMouseEnter={e => { (e.target as HTMLButtonElement).style.color = '#c44'; }}
              onMouseLeave={e => { (e.target as HTMLButtonElement).style.color = '#c0a0a0'; }}
              style={s.delBtn}>✕</button>
          </div>
        ))}
        {cats.length === 0 && !err && (
          <div style={{ color: '#a09888', fontSize: 13, textAlign: 'center', padding: 14 }}>Нет категорий</div>
        )}
      </div>
    </div>
  );
}
